import * as Yup from 'yup'
import { useFormik } from 'formik'
import {
  AcaoLinktree,
  Button,
  FormInput,
  IconeBaixar,
  IconeDispensar,
  IconeValidar,
  LoadingIndicator,
  ORIENTACOES_PERFIL,
  ResumoPrescricaoCard,
  RodapeConfiancaReceita,
  URL_VALIDAR_ICP,
  useModal,
} from '../../../../../components'
import { useThemeMode } from '../../../../../../_metronic/partials'
import { useParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { PrescricaoPacienteView } from '../../../../apps/atendimento_medicos/prescricao/core/_model'
import {
  downloadAuthAPACPrescricao,
  downloadAuthLMEPrescricao,
  downloadAuthPrescricao,
  getPublicPrescricao,
} from '../../core/_request'
import {
  abrirAbaCarregando,
  ButtonType,
  direcionarAbaArquivo,
  fecharAba,
  generatePdfUrl,
  IconPaths,
  IconSize,
  isIOS,
  ModalSize,
  showAlert,
  showError,
} from '../../../../../../_metronic/helpers'
import { UsePrescricaoModal } from '../modal/UsePrescricaoModal'
import { ArquivoDto } from '../../../../apps/atendimento/core/_models'

type DownloadKey = 'prescricao' | 'lme' | 'apac'

export const PrescricaoContent = () => {
  const { showModal, closeModal } = useModal()
  const { updateMode, updateMenuMode } = useThemeMode()
  const { tokenPrescricao } = useParams<{ tokenPrescricao?: string }>()

  const [hasControleEspecial, setHasControleEspecial] = useState<boolean>(false)
  const [pdfPath, setPdfPath] = useState('')
  const [prescricao, setPrescricao] = useState<PrescricaoPacienteView>()
  const [isMobile, setIsMobile] = useState(false)
  const [loadingAcao, setLoadingAcao] = useState<DownloadKey | null>(null)

  const validationSchema = Yup.object().shape({
    tokenPrescricao: Yup.string().required('Código de acesso é obrigatório'),
    tokenPaciente: Yup.string().required('Código do paciente é obrigatório'),
  })

  const formik = useFormik({
    initialValues: {
      tokenPrescricao: tokenPrescricao || '',
      tokenPaciente: '',
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true)
      try {
        const prescricao = await getPublicPrescricao(values.tokenPrescricao, values.tokenPaciente)

        if (!prescricao.valida) {
          showAlert({ title: 'Erro!', text: 'Receita expirada!', icon: 'error' })
          return
        }

        const isEspecial = prescricao.especial || prescricao.retencao

        if (isEspecial && prescricao.utilizada) {
          const dispensa =
            prescricao.dispensacoes && prescricao.dispensacoes.length > 0
              ? prescricao.dispensacoes[0]
              : undefined
          // Valores vêm de formulário público (dispensação) — escapar antes de injetar no HTML do alerta
          const escapeHtml = (value?: string) => {
            const map: Record<string, string> = {
              '&': '&amp;',
              '<': '&lt;',
              '>': '&gt;',
              '"': '&quot;',
              "'": '&#39;',
            }
            return String(value ?? '').replace(/[&<>"']/g, (c) => map[c])
          }
          // Página pública: mascara o CPF do farmacêutico, expondo só os
          // dígitos centrais (LGPD). Mantém o valor original se não for um CPF.
          const mascararCpf = (value?: string) => {
            const digitos = String(value ?? '').replace(/\D/g, '')
            if (digitos.length !== 11) return value ?? ''
            return `***.${digitos.slice(3, 6)}.${digitos.slice(6, 9)}-**`
          }
          const html = dispensa
            ? `
      <div>Dispensada por: ${escapeHtml(dispensa.farmaceutico)}</div>
      <div>CPF: ${escapeHtml(mascararCpf(dispensa.documento))}</div>
      <div>Data: ${escapeHtml(dispensa.dataFormatada)}</div>
      ${dispensa.farmacia ? `<div>Unidade/Farmácia: ${escapeHtml(dispensa.farmacia)}</div>` : ''}`
            : '<div>Receita já dispensada!</div>'

          showAlert({ title: 'Receita já dispensada!', html, icon: 'error' })
          return
        }

        if (isEspecial) setHasControleEspecial(true)
        setPrescricao(prescricao)
      } catch (error) {
        showError(error)
      } finally {
        setSubmitting(false)
      }
    },
  })

  const resetForm = (clearFields = false) => {
    // Libera a object URL do preview de PDF para não vazar memória ("Nova consulta").
    if (pdfPath) URL.revokeObjectURL(pdfPath)
    formik.resetForm({
      values: {
        tokenPrescricao: clearFields ? '' : tokenPrescricao || '',
        tokenPaciente: '',
      },
    })
    setPdfPath('')
    setPrescricao(undefined)
    setHasControleEspecial(false)
    setLoadingAcao(null)
  }

  const showUsePrescricao = () =>
    showModal({
      title: 'Informações do farmacêutico',
      content: (
        <UsePrescricaoModal
          tokenPrescricao={formik.values.tokenPrescricao}
          tokenPaciente={formik.values.tokenPaciente}
          onSuccess={() => {
            closeModal()
            resetForm(true)
          }}
        />
      ),
      size: ModalSize.Medium,
    })

  useEffect(() => {
    updateMenuMode('light')
    updateMode('light')
    const userAgent = navigator.userAgent || navigator.vendor
    // Reconhece iPadOS recente (UA "Macintosh" + toque) via isIOS().
    setIsMobile(/android/i.test(userAgent) || isIOS())
  }, [updateMenuMode, updateMode])

  const setFile = (file: ArquivoDto) => {
    // O backend pode devolver o arquivo como URL pronta OU como base64 inline.
    // (o helper downloadArquivo trata ambos; aqui antes só tratávamos base64,
    // por isso o clique "não fazia nada" quando vinha url).
    if (file?.url) {
      window.open(file.url, '_blank', 'noopener,noreferrer')
      return
    }

    const url = generatePdfUrl(file?.contentBase64, file?.type)
    if (!url) {
      showAlert({
        title: 'Erro!',
        text: 'Não foi possível abrir o documento. Tente novamente.',
        icon: 'error',
      })
      return
    }

    if (isMobile) {
      window.open(url, '_blank')
    } else {
      // Substitui o preview anterior, liberando a object URL antiga.
      setPdfPath((anterior) => {
        if (anterior) URL.revokeObjectURL(anterior)
        return url
      })
    }
  }

  const baixar = async (
    chave: DownloadKey,
    fn: (token: string, paciente: string) => Promise<ArquivoDto>
  ) => {
    setLoadingAcao(chave)
    // No iOS, abrir a aba SÍNCRONA no gesto de toque (antes do await) preserva a
    // user activation; depois ela é redirecionada para o PDF.
    const aba = isIOS() ? abrirAbaCarregando() : null
    try {
      const file = await fn(formik.values.tokenPrescricao, formik.values.tokenPaciente)
      if (isIOS()) {
        if (!aba) {
          showAlert({
            title: 'Erro!',
            text: 'Não foi possível abrir o documento. Verifique se os pop-ups estão liberados e tente novamente.',
            icon: 'error',
          })
        } else {
          direcionarAbaArquivo(aba, file)
        }
      } else {
        setFile(file)
      }
    } catch (error) {
      fecharAba(aba)
      showAlert({
        title: 'Erro!',
        text: 'Não foi possível abrir o documento. Tente novamente.',
        icon: 'error',
      })
    } finally {
      setLoadingAcao(null)
    }
  }

  const handlePrescricao = () => baixar('prescricao', downloadAuthPrescricao)
  const handleLME = () => baixar('lme', downloadAuthLMEPrescricao)
  const handleAPAC = () => baixar('apac', downloadAuthAPACPrescricao)

  return (
    <>
      {prescricao ? (
        <>
          <ResumoPrescricaoCard prescricao={prescricao} />

          <div className='acoes-linktree'>
            <AcaoLinktree
              icon={<IconeBaixar />}
              titulo='Baixar Receita'
              microcopy={ORIENTACOES_PERFIL.paciente}
              loading={loadingAcao === 'prescricao'}
              onClick={handlePrescricao}
            />

            {prescricao.lme && (
              <AcaoLinktree
                icon={<IconeBaixar />}
                titulo={prescricao.lme.descricao || 'Baixar LME'}
                microcopy='Laudo para medicamento especializado.'
                loading={loadingAcao === 'lme'}
                onClick={handleLME}
              />
            )}

            {prescricao.apac && (
              <AcaoLinktree
                icon={<IconeBaixar />}
                titulo={prescricao.apac.descricao || 'Baixar APAC'}
                microcopy='Autorização de procedimento de alta complexidade.'
                loading={loadingAcao === 'apac'}
                onClick={handleAPAC}
              />
            )}

            <AcaoLinktree
              icon={<IconeValidar />}
              titulo='Validar Assinatura'
              microcopy={ORIENTACOES_PERFIL.validador}
              href={URL_VALIDAR_ICP}
              target='_blank'
            />

            {hasControleEspecial && (
              <AcaoLinktree
                icon={<IconeDispensar />}
                titulo='Registrar Dispensação'
                microcopy={ORIENTACOES_PERFIL.farmacia}
                badge='Exclusivo para farmacêutico'
                onClick={showUsePrescricao}
              />
            )}
          </div>

          {pdfPath && (
            <object
              className='mt-5'
              width='100%'
              height='450px'
              data={pdfPath + '#navpanes=0&scrollbar=0'}
              aria-label='Visualizacao da prescricao em PDF'
            >
              Visualizacao da prescricao em PDF
            </object>
          )}

          <Button
            className='btn-secondary btn-auth-full mt-5'
            onClick={resetForm}
            icon={IconPaths.Search}
          >
            Nova consulta
          </Button>

          <RodapeConfiancaReceita />
        </>
      ) : (
        <form className='w-100' onSubmit={formik.handleSubmit} noValidate>
          <FormInput
            formik={formik}
            label='Token de acesso'
            placeholder='Digite o token de acesso'
            name='tokenPrescricao'
            className='mb-8'
            isRequired
            infoText='Token presente na prescrição'
          />
          <FormInput
            formik={formik}
            label='Código de desbloqueio'
            placeholder='Digite o código de desbloqueio'
            name='tokenPaciente'
            className='mb-8'
            isRequired
            infoText='4 primeiros dígitos do CPF do paciente'
          />
          <Button
            type={ButtonType.Submit}
            className='btn-primary btn-auth-full'
            disabled={formik.isSubmitting}
            icon={formik.isSubmitting ? undefined : IconPaths.Eye}
            iconSize={IconSize.Small}
          >
            {formik.isSubmitting ? <LoadingIndicator /> : 'Visualizar'}
          </Button>
          <RodapeConfiancaReceita />
        </form>
      )}
    </>
  )
}
